import { useTranslation } from 'react-i18next';

const phaseColors = [
  { bg: 'bg-[#4F2D63]', light: 'bg-[#F0E8F8]', border: 'border-[#D4B8F0]', text: 'text-[#4F2D63]', line: 'bg-[#D4B8F0]' },
  { bg: 'bg-[#2D6348]', light: 'bg-[#E8F4EE]', border: 'border-[#A8D8B8]', text: 'text-[#2D6348]', line: 'bg-[#A8D8B8]' },
  { bg: 'bg-[#4A3000]', light: 'bg-[#FFF8E8]', border: 'border-[#F0D888]', text: 'text-[#6B4500]', line: 'bg-[#F0D888]' },
];

const phaseIcons = [
  'ri-building-line',
  'ri-bar-chart-grouped-line',
  'ri-funds-line',
];

export default function ThreePhasesSection() {
  const { t } = useTranslation();

  const phases = [1, 2, 3].map((n, i) => ({
    num: n,
    color: phaseColors[i],
    icon: phaseIcons[i],
    label: t(`sllc_ph${n}_label`),
    time: t(`sllc_ph${n}_time`),
    title: t(`sllc_ph${n}_title`),
    desc: t(`sllc_ph${n}_desc`),
    details: [
      t(`sllc_ph${n}_d1`),
      t(`sllc_ph${n}_d2`),
      t(`sllc_ph${n}_d3`),
      t(`sllc_ph${n}_d4`),
      t(`sllc_ph${n}_d5`),
      n === 2 ? t(`sllc_ph${n}_d6`) : null,
    ].filter(Boolean) as string[],
    note: t(`sllc_ph${n}_note`),
  }));

  return (
    <section className="py-16 lg:py-24 bg-[#F8F3FB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <span className="text-[#4F2D63] text-sm font-semibold uppercase tracking-widest">
            {t('sllc_phases_eyebrow')}
          </span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
            {t('sllc_phases_title')}
          </h2>
          <p className="mt-4 text-gray-600 text-sm sm:text-base leading-relaxed">
            {t('sllc_phases_subtitle')}
          </p>
        </div>

        {/* Phases */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 relative">
          {/* Desktop connector line */}
          <div className="hidden lg:block absolute top-[56px] left-[calc(16.666%-1px)] right-[calc(16.666%-1px)] h-0.5 bg-[#E9D9F2] z-0" />

          {phases.map((phase, idx) => (
            <div key={phase.num} className="flex flex-col">
              {/* Mobile: vertical connector line between cards (above phase 2 and 3) */}
              {idx > 0 && (
                <div className="lg:hidden flex justify-center mb-0">
                  <div className={`w-0.5 h-6 ${phase.color.line}`} />
                </div>
              )}

              {/* Phase number circle */}
              <div className="flex justify-center mb-5 relative z-10">
                <div className={`w-14 h-14 flex items-center justify-center rounded-full ${phase.color.bg} flex-shrink-0`}>
                  <i className={`${phase.icon} text-white text-xl`} />
                </div>
              </div>

              {/* Card */}
              <div className="flex-1 bg-white rounded-xl border border-[#E9D9F2] overflow-hidden">
                {/* Header */}
                <div className={`${phase.color.light} border-b ${phase.color.border} px-4 sm:px-6 py-4`}>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <span className={`text-xs font-bold uppercase tracking-wider ${phase.color.text} leading-snug`}>
                      {t('sllc_phase_prefix')} {phase.num} — {phase.label}
                    </span>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${phase.color.border} ${phase.color.light} ${phase.color.text} whitespace-nowrap flex-shrink-0`}>
                      {phase.time}
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 leading-snug">{phase.title}</h3>
                </div>

                {/* Body */}
                <div className="px-4 sm:px-6 py-5">
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">{phase.desc}</p>
                  <ul className="space-y-2.5">
                    {phase.details.map((detail) => (
                      <li key={detail} className="flex items-start gap-2.5">
                        <i className="ri-check-line text-[#4F2D63] text-sm mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700 leading-relaxed">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Note */}
                <div className="mx-4 sm:mx-6 mb-5 px-4 py-3 rounded-lg bg-[#F8F3FB] border border-[#E9D9F2]">
                  <p className="text-xs text-gray-500 leading-relaxed">{phase.note}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
